// Globals
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createWebsite,
  deleteWebsite,
  getWebsites,
  selectWebsites,
  selectWebsitesIsLoading,
  updateWebsite,
  changePosition,
  reorderWebsites,
} from '../../feature';
import { selectChannelId } from '@/modules/Channels';

// Modules
import { Header } from '@/modules/Layout';

// Components
import { DraggableTable, Modal, ModalSize } from '@/components';
import { StyledWebsitesContainerInner } from './WebsitesContainer.styles';
import { WebsiteForm } from '../../components';
import { Button } from '@/ui';
import { PlusIcon, TrashIcon } from '@/icons';

// Hooks
import { useBoolean } from '@/hooks';

// Models
import { IWebsites } from '@/models/websites.model';

// Helpers
import { getWebsitesTableConfig, TWebsiteFormValues, WebsiteFieldsNames, WebsiteFormType } from '../../helpers';

// DTOs
import { ReorderWebsitesDTO, CreateWebsiteDTO, UpdateWebsiteDTO } from '../../dtos';

export const WebsitesContainer = () => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();
  const websites = useAppState(selectWebsites);
  const channelId = useAppState(selectChannelId);
  const isLoading = useAppState(selectWebsitesIsLoading);

  const [formType, setFormType] = useState<WebsiteFormType | null>(null);
  const [selectedWebsite, setSelectedWebsite] = useState<IWebsites | null>(null);
  const { value: isDeleteOpen, setTrue: openDelete, setFalse: closeDelete } = useBoolean();

  useEffect(() => {
    dispatch(getWebsites({ showcaseChannelId: channelId, page: 1, pageSize: 1000 }));
  }, [dispatch, channelId]);

  const handleOpenCreate = () => {
    setSelectedWebsite(null);
    setFormType(WebsiteFormType.CREATE);
  };

  const handleOpenEdit = (record: IWebsites) => {
    setSelectedWebsite(record);
    setFormType(WebsiteFormType.UPDATE);
  };

  const handleOpenDelete = (record: IWebsites) => {
    setSelectedWebsite(record);
    openDelete();
  };

  const handleCloseForm = () => {
    setFormType(null);
    setSelectedWebsite(null);
  };

  const handleCloseDelete = () => {
    closeDelete();
    setSelectedWebsite(null);
  };

  const handleSubmit = useCallback(
    async (values: TWebsiteFormValues) => {
      if (!channelId) return;
      try {
        if (formType === WebsiteFormType.UPDATE && selectedWebsite) {
          const updateDTO = new UpdateWebsiteDTO(values, selectedWebsite.id);
          await dispatch(updateWebsite(updateDTO)).unwrap();
        } else {
          const createDTO = new CreateWebsiteDTO(values, channelId);
          await dispatch(createWebsite(createDTO)).unwrap();
        }
        handleCloseForm();
        dispatch(getWebsites({ showcaseChannelId: channelId, page: 1, pageSize: 1000 }));
      } catch {}
    },
    [dispatch, channelId, formType, selectedWebsite],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedWebsite) return;
    try {
      await dispatch(deleteWebsite({ id: selectedWebsite?.id })).unwrap();
      closeDelete();
      setSelectedWebsite(null);
    } catch {}
  }, [dispatch, closeDelete, selectedWebsite]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!websites || !over || active.id === over?.id) return;
      const oldIndex = websites.findIndex((m) => m.id === active.id);
      const newIndex = websites.findIndex((m) => m.id === over?.id);
      const newPoints = arrayMove(websites, oldIndex, newIndex).map((m, index) => ({ ...m, position: index + 1 }));
      dispatch(changePosition(newPoints));
      const dto = new ReorderWebsitesDTO(newPoints, channelId as number);
      try {
        await dispatch(reorderWebsites(dto));
      } catch {
        dispatch(changePosition(websites));
      }
    },
    [dispatch, websites, channelId],
  );

  const tableConfig = getWebsitesTableConfig(t, handleOpenEdit, handleOpenDelete);

  return (
    <>
      <Header title={t('menu_websites')}>
        <Button type="primary" icon={<PlusIcon />} onClick={handleOpenCreate}>
          {t('create_website')}
        </Button>
      </Header>
      <StyledWebsitesContainerInner>
        <DraggableTable
          dataSource={websites ?? []}
          rowKey="id"
          columns={tableConfig}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          onDragEnd={handleDragEnd}
        />
      </StyledWebsitesContainerInner>
      <WebsiteForm
        isOpen={!!formType}
        isLoading={isLoading}
        formType={formType ?? WebsiteFormType.CREATE}
        initialValues={
          selectedWebsite
            ? {
                [WebsiteFieldsNames.TITLE]: selectedWebsite.title,
                [WebsiteFieldsNames.DESCRIPTION]: selectedWebsite.description ?? '',
                [WebsiteFieldsNames.CHANNEL]: selectedWebsite.channelId,
                [WebsiteFieldsNames.IMAGE_TITLE]: selectedWebsite.imageTitle ?? '',
                [WebsiteFieldsNames.IMAGE]: selectedWebsite?.files?.find((file) => file.metaType === 'image')?.file
                  ?.url,
                [WebsiteFieldsNames.POSITION]: selectedWebsite.position,
                [WebsiteFieldsNames.STATUS]: selectedWebsite.status,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        toggleOpen={handleCloseForm}
      />
      <Modal
        visible={isDeleteOpen}
        title={t('delete_confirm')}
        size={ModalSize.MEDIUM}
        onCancel={handleCloseDelete}
        withFooter
        footer={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button block onClick={handleCloseDelete}>
              {t('cancel_btn')}
            </Button>
            <Button block type="primary" danger icon={<TrashIcon />} loading={isLoading} onClick={handleConfirmDelete}>
              {t('delete_btn')}
            </Button>
          </div>
        }
      />
    </>
  );
};
